// Made-up shipping lines and airlines, and the rates they gave, for judging the design (see lib/sample.ts).
// Real carriers and their rates live in the Serb Mind vault.
import { sampleOnly } from '../../lib/sample';
import type { Tone } from '../../components/tone';
import type { Carrier, CarrierRate } from '@serb/shared';

export type CarrierMode = 'Sea' | 'Air' | 'Road';
export const CARRIER_MODES: CarrierMode[] = ['Sea', 'Air', 'Road'];

export type { Carrier, CarrierRate };

const C = (c: Omit<Carrier, 'aliases'>): Carrier => ({ aliases: [c.name], ...c });

export const CARRIERS: Carrier[] = sampleOnly([
  C({
    id: 'cma-cgm',
    name: 'CMA CGM',
    mode: 'Sea',
    country: 'France',
    city: 'Marseille',
    address: "4 Quai d'Arenc, Marseille, France",
    website: 'www.cmacgm.example',
    contact: 'Jean Dupont',
    email: 'jean.dupont@cmacgm.example',
    phone: '+33 4 555 0100',
    services: ['FCL', 'LCL'],
    cargoTypes: ['General Cargo', 'Dangerous Goods (DG)'],
    coverage: 'Europe, Asia, Middle East',
    notes: 'Main line for Far East to Aqaba.',
    responseTime: 'Within 1 day',
    status: 'Active',
    addedDaysAgo: 900,
  }),
  C({
    id: 'messina-line',
    name: 'Messina Line',
    mode: 'Sea',
    country: 'Italy',
    city: 'Genoa',
    address: 'Via al Porto Antico, Genoa, Italy',
    website: 'www.messinaline.example',
    contact: 'Marco Bianchi',
    email: 'marco@messinaline.example',
    phone: '+39 010 555 0111',
    services: ['FCL', 'LCL'],
    cargoTypes: ['General Cargo'],
    coverage: 'Italy, Spain, Mediterranean',
    notes: 'Rolled one of our bookings over in May.',
    responseTime: 'Within 2 days',
    status: 'Active',
    addedDaysAgo: 700,
  }),
  C({
    id: 'turkon-line',
    name: 'Turkon Line',
    mode: 'Sea',
    country: 'Turkey',
    city: 'Istanbul',
    address: 'Tuzla, Istanbul, Turkey',
    website: 'www.turkon.example',
    contact: 'Elif Yilmaz',
    email: 'elif@turkon.example',
    phone: '+90 216 555 0133',
    services: ['FCL', 'LCL'],
    cargoTypes: ['General Cargo'],
    coverage: 'Turkey, Mediterranean, Black Sea',
    notes: '',
    responseTime: '',
    status: 'Active',
    addedDaysAgo: 400,
  }),
  C({
    id: 'arkas-line',
    name: 'Arkas Line',
    mode: 'Sea',
    country: 'Turkey',
    city: 'Izmir',
    address: 'Alsancak Port, Izmir, Turkey',
    website: 'www.arkas.example',
    contact: 'Can Demir',
    email: 'can.demir@arkas.example',
    phone: '+90 232 555 0144',
    services: ['FCL', 'LCL'],
    cargoTypes: ['General Cargo'],
    coverage: 'Mediterranean, Europe',
    notes: '',
    responseTime: 'Within 1 day',
    status: 'Inactive',
    addedDaysAgo: 500,
  }),
  C({
    id: 'emirates-skycargo',
    name: 'Emirates SkyCargo',
    mode: 'Air',
    country: 'UAE',
    city: 'Dubai',
    address: 'Dubai Airport Free Zone, UAE',
    website: 'www.skycargo.example',
    contact: 'Rashid Khan',
    email: 'r.khan@skycargo.example',
    phone: '+971 4 555 0155',
    services: ['Air'],
    cargoTypes: ['General Cargo', 'Perishable Cargo', 'Dangerous Goods (DG)', 'Express Services'],
    coverage: 'Global',
    notes: '',
    responseTime: 'Within 1 day',
    status: 'Active',
    addedDaysAgo: 800,
  }),
  C({
    id: 'royal-jordanian-cargo',
    name: 'Royal Jordanian Cargo',
    mode: 'Air',
    country: 'Jordan',
    city: 'Amman',
    address: 'Queen Alia International Airport, Amman, Jordan',
    website: 'www.rj.example',
    contact: 'Omar Haddad',
    email: 'cargo@rj.example',
    phone: '+962 6 510 0000',
    services: ['Air'],
    cargoTypes: ['General Cargo', 'Perishable Cargo', 'Dangerous Goods (DG)', 'Live Animals', 'Express Services'],
    coverage: 'Global (Europe, Asia, Middle East)',
    notes: 'Reliable for Europe and GCC routes. Good transit times and competitive rates for general cargo.',
    responseTime: 'Within 1 day',
    status: 'Active',
    addedDaysAgo: 950,
  }),
  C({
    id: 'china-eastern-cargo',
    name: 'China Eastern Cargo',
    mode: 'Air',
    country: 'China',
    city: 'Shanghai',
    address: 'Pudong Airport, Shanghai, China',
    website: 'www.ceair.example',
    contact: 'Chen Hao',
    email: 'chen@ceair.example',
    phone: '+86 21 5555 0177',
    services: ['Air'],
    cargoTypes: ['General Cargo'],
    coverage: 'China, Asia',
    notes: '',
    responseTime: '',
    status: 'Active',
    addedDaysAgo: 20,
  }),
  C({
    id: 'qatar-cargo',
    name: 'Qatar Airways Cargo',
    mode: 'Air',
    country: 'Qatar',
    city: 'Doha',
    address: 'Hamad Airport, Doha, Qatar',
    website: 'www.qrcargo.example',
    contact: 'Sara Al Thani',
    email: 'sara@qrcargo.example',
    phone: '+974 4 555 0188',
    services: ['Air'],
    cargoTypes: ['General Cargo', 'Perishable Cargo'],
    coverage: 'Global',
    notes: '',
    responseTime: 'Within 1 day',
    status: 'Active',
    addedDaysAgo: 600,
  }),
]);

export const carrierById = (id: string) => CARRIERS.find((c) => c.id === id);

export type RateOutcome = 'Selected' | 'Not Selected' | 'Pending';
export const RATE_TONE: Record<RateOutcome, Tone> = {
  Selected: 'success',
  'Not Selected': 'error',
  Pending: 'warning',
};

const R = (
  carrierId: string,
  date: string,
  route: string,
  service: CarrierRate['service'],
  rate: number,
  currency: CarrierRate['currency'],
  per: CarrierRate['per'],
  validDays: number,
  outcome: RateOutcome,
): CarrierRate => ({
  carrierId,
  date,
  route,
  service,
  rate,
  currency,
  per,
  validDays,
  outcome,
});

export const CARRIER_RATES: CarrierRate[] = sampleOnly([
  R('royal-jordanian-cargo', '2025-07-20', 'Amman → Dubai', 'Air', 5.6, 'USD', 'kg', 7, 'Selected'),
  R('royal-jordanian-cargo', '2025-07-18', 'Frankfurt → Amman', 'Air', 4.9, 'EUR', 'kg', 5, 'Not Selected'),
  R('royal-jordanian-cargo', '2025-07-15', 'Milan → Amman', 'Air', 5.2, 'EUR', 'kg', 7, 'Selected'),
  R('royal-jordanian-cargo', '2025-07-10', 'Shanghai → Amman', 'Air', 6.1, 'USD', 'kg', 7, 'Not Selected'),
  R('emirates-skycargo', '2025-07-20', 'Amman → Dubai', 'Air', 5.4, 'USD', 'kg', 7, 'Not Selected'),
  R('emirates-skycargo', '2025-07-18', 'Frankfurt → Amman', 'Air', 5.1, 'EUR', 'kg', 5, 'Not Selected'),
  R('emirates-skycargo', '2025-07-12', 'Frankfurt → Dubai', 'Air', 5.6, 'USD', 'kg', 7, 'Selected'),
  R('qatar-cargo', '2025-07-18', 'Frankfurt → Amman', 'Air', 5.3, 'EUR', 'kg', 5, 'Not Selected'),
  R('china-eastern-cargo', '2025-07-23', 'Guangzhou → Amman', 'Air', 5.7, 'USD', 'kg', 7, 'Selected'),
  R('cma-cgm', '2025-07-16', 'Shanghai → Aqaba', 'FCL', 6900, 'USD', 'container', 14, 'Selected'),
  R('cma-cgm', '2025-07-17', 'Valencia → Aqaba', 'FCL', 3050, 'USD', 'container', 14, 'Not Selected'),
  R('messina-line', '2025-07-16', 'Shanghai → Aqaba', 'FCL', 7150, 'USD', 'container', 14, 'Not Selected'),
  R('messina-line', '2025-07-18', 'Marseille → Aqaba', 'LCL', 120, 'USD', 'CBM', 10, 'Selected'),
  R('turkon-line', '2025-07-17', 'Valencia → Aqaba', 'FCL', 2980, 'USD', 'container', 14, 'Selected'),
  R('arkas-line', '2025-07-19', 'Istanbul → Aqaba', 'LCL', 95, 'USD', 'CBM', 10, 'Selected'),
]);
